/**
 * BusinessVerificationTab — Super Admin review center for CARTAL Business
 * ──────────────────────────────────────────────────────────────────────
 * Talks to /api/platform/verification/admin/*
 *   - GET  /submissions?status=<state>
 *   - GET  /submissions/{id}                    → submission + business + docs + events
 *   - GET  /documents/{doc_id}/download        → private document bytes
 *   - POST /submissions/{id}/start-review
 *   - POST /submissions/{id}/approve            (توثيق وتفعيل)
 *   - POST /submissions/{id}/reject
 *   - POST /submissions/{id}/request-info
 *   - POST /submissions/{id}/suspend            (تعليق نشاط موثّق)
 *   - POST /submissions/{id}/revoke
 *   - POST /submissions/{id}/notes
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  ShieldCheck, Loader2, RefreshCw, CheckCircle2, XCircle, AlertTriangle,
  Eye, Building2, FileText, MessageSquare, Pause, Ban, Info, Download, Clock,
  X, ExternalLink, Trash2, Zap,
} from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { useAuth } from "../../lib/auth";

const STATE_STYLE = {
  draft:           { ar: "مسودة",             en: "Draft",           bg: "#e5e7eb", fg: "#374151" },
  submitted:       { ar: "قيد المراجعة",       en: "Submitted",       bg: "rgba(201,165,92,0.15)", fg: "#8a6f2e" },
  under_review:    { ar: "قيد التدقيق",         en: "Under Review",    bg: "rgba(59,130,246,0.15)", fg: "#1d4ed8" },
  needs_more_docs: { ar: "استكمال بيانات",      en: "Needs Docs",      bg: "rgba(217,119,6,0.15)",  fg: "#a16207" },
  approved:        { ar: "موثّق",             en: "Approved",        bg: "rgba(10,79,58,0.12)",  fg: "#0a4f3a" },
  rejected:        { ar: "مرفوض",             en: "Rejected",        bg: "rgba(185,28,28,0.10)", fg: "#b91c1c" },
  suspended:       { ar: "معلّق",             en: "Suspended",       bg: "rgba(120,113,108,0.20)", fg: "#57534e" },
  revoked:         { ar: "مسحوب",             en: "Revoked",         bg: "rgba(185,28,28,0.15)",  fg: "#7f1d1d" },
  needs_reverify:  { ar: "يحتاج إعادة توثيق",   en: "Needs Reverify",  bg: "rgba(217,119,6,0.15)",  fg: "#a16207" },
};

const DOC_TYPE_LABEL = {
  commercial_registration:  { ar: "السجل التجاري",     en: "CR" },
  national_business_number: { ar: "الرقم الوطني الموحد", en: "Nat'l Number" },
  vat_certificate:          { ar: "الرقم الضريبي",     en: "VAT" },
  business_license:         { ar: "الترخيص",           en: "License" },
  national_address_proof:   { ar: "إثبات العنوان الوطني", en: "Nat'l Address" },
  authorized_rep_id:        { ar: "هوية الممثل",       en: "Rep ID" },
  authorization_letter:     { ar: "خطاب تفويض",       en: "Auth Letter" },
  iban_certificate:         { ar: "شهادة IBAN",       en: "IBAN" },
  other:                    { ar: "أخرى",             en: "Other" },
};

export default function BusinessVerificationTab() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [status, setStatus] = useState("submitted");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [detail, setDetail] = useState(null); // {submission, business, documents, events}
  const [action, setAction] = useState(null); // { verifId, kind }
  const [note, setNote] = useState("");
  const [acting, setActing] = useState(false);
  // iter196 — delete-draft confirmation state
  const [pendingDelete, setPendingDelete] = useState(null); // {id, label}
  const [deleting, setDeleting] = useState(false);

  const reload = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await apiClient.get("/platform/verification/admin/submissions", {
        params: status === "all" ? {} : { status },
      });
      setItems(data.items || []);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }, [status]);

  useEffect(() => { reload(); }, [reload]);

  const openDetail = async (verifId) => {
    setErr(null);
    try {
      // Auto-transition submitted → under_review when admin opens (idempotent)
      const { data } = await apiClient.get(`/platform/verification/admin/submissions/${verifId}`);
      setDetail(data);
      if (data.submission?.status === "submitted") {
        try { await apiClient.post(`/platform/verification/admin/submissions/${verifId}/start-review`); }
        catch { /* non-fatal */ }
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const downloadDoc = async (docId, filename) => {
    // iter190 bug-fix: previously a bare `catch {}` swallowed the real
    // server error which produced the misleading "تعذر تحميل الوثيقة"
    // toast for ANY failure. We now:
    //   1) decode blob error responses so the actual reason surfaces,
    //   2) open the file in a new tab as a fallback preview so admins
    //      can still view it even when the browser refuses the auto
    //      download (e.g. Safari mobile).
    try {
      const res = await apiClient.get(
        `/platform/verification/admin/documents/${docId}/download`,
        { responseType: "blob", validateStatus: (s) => s >= 200 && s < 300 },
      );
      const mime = res.data?.type || res.headers?.["content-type"] || "application/octet-stream";
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      // Try programmatic download first
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "document";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Give the browser a beat before revoking, so slower devices
      // finish the download stream.
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      // Try to decode the server's JSON error body (comes back as a
      // Blob because we asked for one).
      let msg = null;
      try {
        const b = e?.response?.data;
        if (b instanceof Blob) {
          const txt = await b.text();
          try {
            const parsed = JSON.parse(txt);
            msg = formatApiError(parsed?.detail) || parsed?.message || null;
          } catch { msg = txt || null; }
        } else if (b) {
          msg = formatApiError(b?.detail) || b?.message || null;
        }
      } catch { /* noop */ }
      toast.error(
        msg
          ? (lang === "ar" ? `تعذّر تحميل الوثيقة: ${msg}` : `Download failed: ${msg}`)
          : (lang === "ar" ? "تعذّر تحميل الوثيقة" : "Download failed"),
      );
    }
  };

  // Alternative preview action — opens the document inline in a new
  // tab. Used from the details modal so admins have a second path when
  // the auto-download is blocked. iter190.
  const previewDoc = async (docId) => {
    try {
      const res = await apiClient.get(
        `/platform/verification/admin/documents/${docId}/download`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(lang === "ar" ? "تعذّر معاينة الوثيقة" : "Preview failed");
    }
  };

  const submitAction = async () => {
    if (!action) return;
    if (["reject", "request-info", "suspend", "revoke"].includes(action.kind) && !note.trim()) {
      toast.error(lang === "ar" ? "اكتب سببًا واضحًا." : "A reason is required.");
      return;
    }
    setActing(true);
    try {
      await apiClient.post(
        `/platform/verification/admin/submissions/${action.verifId}/${action.kind}`,
        { reason: note.trim() || null },
      );
      toast.success(lang === "ar" ? "تم." : "Done.");
      setAction(null); setNote(""); setDetail(null);
      await reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setActing(false); }
  };

  // iter196 — delete a DRAFT submission after explicit confirmation.
  // Backend enforces status === "draft"; frontend also gates the button
  // so non-draft rows can never trigger the request.
  const confirmDeleteDraft = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/platform/verification/admin/submissions/${pendingDelete.id}`);
      toast.success(lang === "ar" ? "تم حذف المسودة." : "Draft deleted.");
      setPendingDelete(null);
      await reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setDeleting(false); }
  };

  return (
    <div className="space-y-5" data-testid="business-verification-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl text-[#0a0a0a] inline-flex items-center gap-2">
            <Building2 className="w-6 h-6 text-[#8a6f2e]" />
            {lang === "ar" ? "توثيق شركات كرتال أعمال" : "CARTAL Business Verification"}
          </h2>
          <p className="text-sm text-[#6b6b6b] mt-1">
            {lang === "ar"
              ? "راجع بيانات المنشآت ووثائقها الرسمية. تفعيل شارة \"موثّق\" حصريّ لـ Super Admin."
              : "Review business submissions & official documents. Approving is Super Admin only."}
          </p>
        </div>
        <button onClick={reload} disabled={busy} className="btn-ghost text-sm" data-testid="bizverif-refresh">
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {lang === "ar" ? "تحديث" : "Refresh"}
        </button>
      </div>

      {/* Status filter */}
      <div className="flex flex-wrap gap-2" data-testid="bizverif-status-filter">
        {["submitted", "under_review", "needs_more_docs", "approved", "rejected", "suspended", "all"].map((s) => {
          const conf = STATE_STYLE[s] || { ar: s, en: s, bg: "#eee", fg: "#333" };
          const active = status === s;
          const label = s === "all" ? (lang === "ar" ? "الكل" : "All") : (lang === "ar" ? conf.ar : conf.en);
          return (
            <button key={s} onClick={() => setStatus(s)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition-all ${active ? "shadow-sm" : "opacity-70 hover:opacity-100"}`}
              style={active
                ? { backgroundColor: conf.fg, borderColor: conf.fg, color: "white" }
                : { backgroundColor: "white", borderColor: "#0a0a0a14", color: "#3a3a3a" }}
              data-testid={`bizverif-filter-${s}`}>
              {label}
            </button>
          );
        })}
      </div>

      {err && <div className="rounded-xl p-3 text-sm text-red-700 border border-red-200 bg-red-50">{String(err)}</div>}

      {/* List */}
      <div className="space-y-2">
        {busy && items.length === 0 ? (
          <div className="text-center py-12 text-[#6b6b6b]"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : items.length === 0 ? (
          <div className="surface rounded-2xl p-10 text-center text-[#6b6b6b]" data-testid="bizverif-empty">
            {lang === "ar" ? "لا توجد طلبات مطابقة." : "No submissions."}
          </div>
        ) : items.map((sub) => {
          const conf = STATE_STYLE[sub.status] || {};
          return (
            <div key={sub.id} className="surface rounded-2xl p-4" data-testid={`bizverif-item-${sub.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="inline-flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#0a0a0a] text-sm">
                      {sub.contact?.representative_name || "—"}
                    </span>
                    <code className="text-[10px] bg-[#fafaf7] px-1 rounded text-[#525252]">{sub.business_id?.slice(0, 8)}</code>
                    <span className="text-[10px] font-bold uppercase rounded-full px-2 py-0.5" style={{ backgroundColor: conf.bg, color: conf.fg }}>
                      {lang === "ar" ? conf.ar : conf.en}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#6b6b6b] mt-1 flex flex-wrap gap-x-3">
                    {sub.contact?.official_email && <span>📧 {sub.contact.official_email}</span>}
                    {sub.contact?.official_phone && <span dir="ltr">📱 {sub.contact.official_phone}</span>}
                    {sub.submitted_at && (
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(sub.submitted_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB")}</span>
                    )}
                  </div>
                  {sub.decision_reason && (
                    <div className="text-[11px] text-[#525252] mt-1 italic">
                      {lang === "ar" ? "ملاحظة الإدارة:" : "Admin note:"} {sub.decision_reason}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {sub.status === "draft" && (
                    <button
                      onClick={() => setPendingDelete({ id: sub.id, label: sub.contact?.representative_name || sub.business_id?.slice(0, 8) })}
                      className="inline-flex items-center gap-1.5 rounded-full h-8 px-3 text-xs font-bold border border-red-300 text-red-700 hover:bg-red-50"
                      data-testid={`bizverif-delete-draft-${sub.id}`}
                      title={lang === "ar" ? "حذف المسودة" : "Delete draft"}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      {lang === "ar" ? "حذف المسودة" : "Delete draft"}
                    </button>
                  )}
                  <button onClick={() => openDetail(sub.id)} className="btn-ghost text-xs" data-testid={`bizverif-open-${sub.id}`}>
                    <Eye className="w-3.5 h-3.5" /> {lang === "ar" ? "فتح المراجعة" : "Open review"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail modal */}
      {detail && (
        <Modal onClose={() => setDetail(null)} testid="bizverif-detail"
               title={lang === "ar" ? "تفاصيل طلب التوثيق" : "Verification details"}
               lang={lang}>
          <DetailPanel detail={detail} lang={lang}
                       onDownload={downloadDoc}
                       onPreview={previewDoc}
                       isSuperAdmin={isSuperAdmin}
                       onAction={(kind) => { setAction({ verifId: detail.submission.id, kind }); setNote(""); }} />
        </Modal>
      )}

      {/* Action modal */}
      {action && (
        <Modal onClose={() => { setAction(null); setNote(""); }} testid="bizverif-action"
               title={lang === "ar" ? "تأكيد الإجراء" : "Confirm action"}
               lang={lang}>
          <ActionPanel kind={action.kind} note={note} setNote={setNote} onCancel={() => { setAction(null); setNote(""); }} onConfirm={submitAction} acting={acting} lang={lang} />
        </Modal>
      )}

      {/* iter196 — Delete draft confirmation */}
      {pendingDelete && (
        <Modal onClose={() => !deleting && setPendingDelete(null)} testid="bizverif-delete-confirm"
               title={lang === "ar" ? "تأكيد حذف المسودة" : "Confirm delete draft"}
               lang={lang}>
          <div className="p-6 space-y-4">
            <p className="text-sm text-[#0a0a0a]">
              {lang === "ar"
                ? "سيتم حذف هذه المسودة نهائياً مع جميع الوثائق المرفقة بها. لا يمكن التراجع عن هذه العملية."
                : "This draft (and all uploaded documents) will be permanently deleted. This cannot be undone."}
            </p>
            {pendingDelete.label && (
              <div className="text-xs text-[#525252] bg-[#fafaf7] rounded-md p-2 border border-[#0a0a0a]/[0.05]">
                <span className="font-semibold">{lang === "ar" ? "المسودة:" : "Draft:"}</span> {pendingDelete.label}
              </div>
            )}
            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={() => setPendingDelete(null)} disabled={deleting}
                      className="btn-ghost text-sm" data-testid="bizverif-delete-cancel">
                {lang === "ar" ? "إلغاء" : "Cancel"}
              </button>
              <button onClick={confirmDeleteDraft} disabled={deleting}
                      className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-60"
                      data-testid="bizverif-delete-confirm-btn">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {lang === "ar" ? "حذف نهائي" : "Delete permanently"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}


function DetailPanel({ detail, lang, onDownload, onPreview, onAction, isSuperAdmin }) {
  const { submission: sub, business: biz, documents = [], events = [] } = detail;
  const conf = STATE_STYLE[sub.status] || {};
  return (
    <>
      <div className="flex items-center gap-2 mb-2">
        <Building2 className="w-5 h-5 text-[#8a6f2e]" />
        <h3 className="font-display text-xl">{biz?.name_ar || "—"}</h3>
        <span className="text-[10px] font-bold uppercase rounded-full px-2 py-0.5" style={{ backgroundColor: conf.bg, color: conf.fg }}>
          {lang === "ar" ? conf.ar : conf.en}
        </span>
      </div>
      <div className="text-[12px] text-[#525252] mb-4 flex flex-wrap gap-x-3">
        {biz?.category && <span><strong>{lang === "ar" ? "نوع النشاط:" : "Category:"}</strong> {biz.category}</span>}
        {biz?.slug && <span><strong>slug:</strong> <code className="bg-[#fafaf7] px-1 rounded">{biz.slug}</code></span>}
      </div>

      <section className="mb-5">
        <h4 className="text-[11px] uppercase tracking-widest text-[#8a6f2e] font-bold mb-2">
          {lang === "ar" ? "بيانات المنشأة" : "Business info"}
        </h4>
        <dl className="grid sm:grid-cols-2 gap-3 text-[13px]">
          {[
            ["representative_name", lang === "ar" ? "المسؤول" : "Representative"],
            ["representative_national_id", lang === "ar" ? "هوية المسؤول" : "Rep National ID"],
            ["representative_phone", lang === "ar" ? "جوال المسؤول" : "Rep Phone"],
            ["official_phone", lang === "ar" ? "هاتف رسمي" : "Official phone"],
            ["official_email", lang === "ar" ? "بريد رسمي" : "Official email"],
            ["website", lang === "ar" ? "الموقع" : "Website"],
            ["official_address_ar", lang === "ar" ? "العنوان" : "Address"],
          ].map(([k, lbl]) => (
            <div key={k}>
              <dt className="text-[10px] uppercase text-[#8a8a8a]">{lbl}</dt>
              <dd className="text-[#0a0a0a]" dir={k.endsWith("phone") ? "ltr" : "auto"}>{(sub.contact || {})[k] || "—"}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mb-5">
        <h4 className="text-[11px] uppercase tracking-widest text-[#8a6f2e] font-bold mb-2">
          {lang === "ar" ? "الوثائق المرفوعة" : "Uploaded documents"}
        </h4>
        {documents.length === 0 ? (
          <div className="text-[12px] text-[#8a8a8a]">{lang === "ar" ? "لا توجد وثائق." : "No documents."}</div>
        ) : (
          <div className="space-y-2">
            {documents.map((d) => {
              const tl = DOC_TYPE_LABEL[d.doc_type] || { ar: d.doc_type, en: d.doc_type };
              return (
                <div key={d.id} className="rounded-lg border border-[#0a0a0a]/[0.08] p-3 flex items-center gap-3 flex-wrap">
                  <FileText className="w-4 h-4 text-[#8a6f2e]" />
                  <div className="text-[13px] font-semibold text-[#0a0a0a]">{lang === "ar" ? tl.ar : tl.en}</div>
                  {d.doc_number && <code className="text-[11px] bg-[#fafaf7] px-1 rounded">{d.doc_number}</code>}
                  {d.expires_at && (
                    <span className="text-[11px] text-[#8a6f2e]">{lang === "ar" ? "ينتهي:" : "expires:"} {d.expires_at}</span>
                  )}
                  <span className="text-[10px] text-[#8a8a8a] ms-auto" dir="ltr">{Math.round((d.size_bytes||0)/1024)} KB</span>
                  <button onClick={() => onPreview(d.id)}
                          className="text-[11px] font-bold text-[#0a0a0a] inline-flex items-center gap-1 hover:underline"
                          data-testid={`bizverif-preview-${d.id}`}
                          title={lang === "ar" ? "معاينة" : "Preview"}>
                    <ExternalLink className="w-3 h-3" /> {lang === "ar" ? "معاينة" : "Preview"}
                  </button>
                  <button onClick={() => onDownload(d.id, d.original_filename)}
                          className="text-[11px] font-bold text-[#0a0a0a] inline-flex items-center gap-1 hover:underline"
                          data-testid={`bizverif-download-${d.id}`}>
                    <Download className="w-3 h-3" /> {lang === "ar" ? "تحميل" : "Download"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {events.length > 0 && (
        <section className="mb-5">
          <h4 className="text-[11px] uppercase tracking-widest text-[#8a6f2e] font-bold mb-2">
            {lang === "ar" ? "سجل النشاط" : "Audit log"}
          </h4>
          <ul className="text-[11px] text-[#525252] space-y-0.5 max-h-40 overflow-y-auto">
            {events.map((e) => (
              <li key={e.id || e.at}><code>{e.at?.slice(0, 19)}</code> · <strong>{e.kind}</strong> · {e.actor_role}</li>
            ))}
          </ul>
        </section>
      )}

      <div className="flex flex-wrap gap-2 pt-3 border-t border-[#0a0a0a]/[0.06]">
        {["submitted", "under_review", "needs_more_docs", "rejected", "suspended"].includes(sub.status) && (
          <button onClick={() => onAction("approve")}
                  className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-xs font-bold text-white bg-[#0a4f3a] hover:bg-[#0f5f47]"
                  data-testid="bizverif-approve">
            <CheckCircle2 className="w-4 h-4" /> {lang === "ar" ? "توثيق وتفعيل" : "Verify & Activate"}
          </button>
        )}
        {["submitted", "under_review", "needs_more_docs"].includes(sub.status) && (
          <>
            <button onClick={() => onAction("request-info")}
                    className="btn-ghost text-xs" data-testid="bizverif-request-info">
              <Info className="w-3.5 h-3.5" /> {lang === "ar" ? "طلب استكمال" : "Request info"}
            </button>
            <button onClick={() => onAction("reject")}
                    className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-xs font-bold border border-red-200 text-red-700 hover:bg-red-50"
                    data-testid="bizverif-reject">
              <XCircle className="w-4 h-4" /> {lang === "ar" ? "رفض الطلب" : "Reject"}
            </button>
          </>
        )}
        {sub.status === "approved" && (
          <button onClick={() => onAction("suspend")}
                  className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-xs font-bold border border-amber-300 text-amber-800 hover:bg-amber-50"
                  data-testid="bizverif-suspend">
            <Pause className="w-4 h-4" /> {lang === "ar" ? "تعليق/تجميد" : "Suspend"}
          </button>
        )}
        {["approved", "suspended"].includes(sub.status) && (
          <button onClick={() => onAction("revoke")}
                  className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-xs font-bold border border-red-300 text-red-800 hover:bg-red-50"
                  data-testid="bizverif-revoke">
            <Ban className="w-4 h-4" /> {lang === "ar" ? "سحب التوثيق" : "Revoke"}
          </button>
        )}

        {/*
          Super-admin-only exceptional override — "Approve Anyway".
          Present alongside the standard actions but visually distinct so
          it is never mistaken for the normal workflow. Skipped when the
          submission is already approved (nothing to override) and hidden
          for every non-super_admin user (backend also enforces this).
        */}
        {isSuperAdmin && sub.status !== "approved" && (
          <button onClick={() => onAction("force-approve")}
                  className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-xs font-bold border-2 border-dashed border-[#8a6f2e] text-[#8a6f2e] hover:bg-[#fdf6e3] ml-auto"
                  data-testid="bizverif-force-approve"
                  title={lang === "ar"
                    ? "إجراء استثنائي — لصاحب الحساب المعروف شخصياً"
                    : "Exceptional override — for personally known account owner"}>
            <Zap className="w-4 h-4" /> {lang === "ar" ? "توثيق على أي حال" : "Approve Anyway"}
          </button>
        )}
      </div>
    </>
  );
}


function ActionPanel({ kind, note, setNote, onCancel, onConfirm, acting, lang }) {
  const meta = {
    approve:        { ar: "توثيق وتفعيل", en: "Verify & Activate", desc: lang === "ar" ? "سيتم تفعيل النشاط رسميًا وإظهار شارة موثّق." : "The business will be marked verified.", danger: false },
    "force-approve":{ ar: "توثيق على أي حال — إجراء استثنائي", en: "Approve Anyway — exceptional", desc: lang === "ar" ? "سيتم توثيق هذا النشاط مباشرةً حتى لو كان طلبه غير مكتمل أو بدون وثائق. استخدم هذا الخيار فقط للحسابات التي تعرفها شخصياً. سيُسجَّل الإجراء في سجل الإدارة." : "Bypasses missing-docs / draft checks. Use only for personally known account owners. Recorded in the audit log.", danger: true },
    reject:         { ar: "رفض الطلب",   en: "Reject",             desc: lang === "ar" ? "اكتب سبب الرفض بوضوح — يظهر لصاحب الطلب." : "Reason is shown to the applicant.", danger: true },
    "request-info": { ar: "طلب استكمال بيانات", en: "Request more info", desc: lang === "ar" ? "أخبر صاحب الطلب بما ينقصه." : "Tell the applicant what's missing.", danger: false },
    suspend:        { ar: "تعليق النشاط", en: "Suspend",            desc: lang === "ar" ? "يتم إيقاف التوثيق مؤقتًا حتى تراجع." : "Verification is paused pending review.", danger: true },
    revoke:         { ar: "سحب التوثيق", en: "Revoke",              desc: lang === "ar" ? "يتم سحب التوثيق نهائيًا." : "Verification is fully revoked.", danger: true },
  }[kind] || { ar: kind, en: kind, desc: "", danger: false };

  return (
    <>
      <h3 className="font-display text-xl mb-1 inline-flex items-center gap-2">
        {meta.danger ? <AlertTriangle className="w-5 h-5 text-red-700" /> : <CheckCircle2 className="w-5 h-5 text-[#0a4f3a]" />}
        {lang === "ar" ? meta.ar : meta.en}
      </h3>
      <p className="text-sm text-[#525252] mb-3">{meta.desc}</p>
      <label className="block text-xs font-bold text-[#3a3a3a] mb-1">
        {(kind === "approve" || kind === "force-approve")
          ? (lang === "ar" ? "ملاحظات (اختياري)" : "Notes (optional)")
          : (lang === "ar" ? "السبب (مطلوب)" : "Reason (required)")}
      </label>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} maxLength={600}
                className="input-dark" data-testid="bizverif-note" />
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onCancel} className="btn-ghost text-sm">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
        <button onClick={onConfirm} disabled={acting || (kind !== "approve" && kind !== "force-approve" && !note.trim())}
                className="inline-flex items-center gap-1.5 rounded-full h-10 px-5 text-sm font-bold text-white disabled:opacity-50"
                style={{ backgroundColor: meta.danger ? "#b91c1c" : "#0a4f3a" }}
                data-testid="bizverif-confirm">
          {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          {lang === "ar" ? "تأكيد" : "Confirm"}
        </button>
      </div>
    </>
  );
}


function Modal({ children, onClose, testid, title, lang }) {
  // iter190 bug-fix: added a visible ✕ close button in the modal
  // header. Backdrop click + ESC key still close the modal. Closing
  // simply invokes onClose(); the parent's `detail` / `action` state
  // is set to null which restores the list view with pagination and
  // filters intact (those live in independent hooks).
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div onClick={onClose} className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(10,10,10,0.7)" }} data-testid={testid}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto shadow-2xl">
        <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-3 border-b border-[#0a0a0a]/[0.08] z-10">
          <div className="text-[13px] font-semibold text-[#3a3a3a]">
            {title || (lang === "ar" ? "التفاصيل" : "Details")}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={lang === "ar" ? "إغلاق" : "Close"}
            title={lang === "ar" ? "إغلاق" : "Close"}
            className="p-2 -me-2 rounded-full hover:bg-[#0a0a0a]/[0.05] text-[#525252] hover:text-[#0a0a0a]"
            data-testid={`${testid}-close`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
}
